import { create } from 'zustand';
import { IGetAssetColorRepo, ISetAssetColorRepo } from '../interfaces';

export const COLORS = [
  '#FF0000',
  '#006B6B',
  '#7BA81C',
  '#B500B5',
  '#FF8787',
  '#2E96FF',
  '#223499',
  '#FF7A00',
  '#B50000',
  '#17A68C',
  '#8F8A00',
  '#8A2BE2',
  '#3477DB',
  '#D48364',
  '#800000',
  '#03A0B5',
  '#BC8A0B',
  '#483D8B',
  '#0F6C78',
  '#076DBB',
  '#B8540B',
  '#886288',
];

const INDEX_COLORS = {
  '#0000E0': 'cdi:idx',
  '#D47500': 'ibov:idx',
  '#DB0A5B': 'ipca:idx',
  '#600060': 'poup:idx',
  '#00AA00': 'selic:idx',
  '#527AC2': 'dolar:idx',
  '#939393': 'ibxx:idx',
  '#6B8E23': 'ima-b:idx',
  '#FF00FF': 'ihfa:idx',
  '#8D6708': 'igp-m:idx',
  '#E26A6A': 'irf-m:idx',
  '#1D781D': 'idiv:idx',
  '#545454': 'smll:idx',
  '#8778E8': 'sp500-tr:idx',
  '#009739': 'sp500-brl:idx',
};

export class AssetColorRepo implements IGetAssetColorRepo, ISetAssetColorRepo {
  private assetColors: Record<string, string>;

  constructor() {
    this.assetColors = COLORS.reduce(
      (total, current) => ({ ...total, [current]: null }),
      {}
    );
  }

  setColor(id: string): string {
    const current = Object.values(this.assetColors).find((fund) => fund === id);

    if (current) {
      return this.getColor(id);
    }

    try {
      const [emptyColor] = Object.entries(this.assetColors).find(
        ([, fund]) => !fund
      );

      this.assetColors[emptyColor] = id;
      return emptyColor;
    } catch (error) {
      return '#333';
    }
  }

  getColor(id: string): string {
    try {
      const [color] = Object.entries(this.assetColors).find(
        ([, fund]) => fund === id
      );

      return color;
    } catch (error) {
      return '#333';
    }
  }

  removeColor(id: string): void {
    try {
      const [cleanColor] = Object.entries(this.assetColors).find(
        ([, fund]) => fund === id
      );

      this.assetColors = { ...this.assetColors, [cleanColor]: null };
    } catch (error) {
      return;
    }
  }

  assetHasColor(id: string): boolean {
    try {
      const [color] = Object.entries(this.assetColors).find(
        ([, fund]) => fund === id
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  resetColors(): void {
    this.assetColors = COLORS.reduce(
      (total, current) => ({ ...total, [current]: null }),
      {}
    );
  }
}

const useAssetColorStore = create<{ assetColors: Record<string, string> }>(
  () => ({
    assetColors: {},
  })
);

export class AssetColorRepoZustend
  implements IGetAssetColorRepo, ISetAssetColorRepo
{
  constructor() {
    const assetColors = {
      ...COLORS.reduce((total, current) => ({ ...total, [current]: null }), {}),
      ...INDEX_COLORS,
    };

    useAssetColorStore.setState({ assetColors });
  }

  private getZustendColor() {
    return useAssetColorStore.getState().assetColors;
  }

  private setZustendColor(newColors: Record<string, string>) {
    return useAssetColorStore.setState((state) => ({
      assetColors: { ...state.assetColors, ...newColors },
    }));
  }

  getColor(id: string): string {
    try {
      const [color] = Object.entries(this.getZustendColor()).find(
        ([, fund]) => fund === id
      );

      return color;
    } catch (error) {
      return '#333';
    }
  }

  setColor(id: string): string {
    const current = Object.values(this.getZustendColor()).find(
      (fund) => fund === id
    );

    if (current) {
      return this.getColor(id);
    }

    try {
      const [emptyColor] = Object.entries(this.getZustendColor()).find(
        ([, fund]) => !fund
      );

      this.setZustendColor({ [emptyColor]: id });

      return emptyColor;
    } catch (error) {
      return '#333';
    }
  }
}
